import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { projectById } from "../data/projects";
import { home, team, publicUrl } from "../content";

const heroProjects = home.hero.slides
  .map((slide) => projectById[slide])
  .filter(Boolean);

const expertise = home.expertise.items;

const founders = team.items.map((member) => ({ ...member, image: publicUrl(member.image) }));

const resolveProjects = (items) => items
  .map((item) => ({ ...item, project: projectById[item.project] }))
  .filter((item) => item.project);

const featuredProjects = resolveProjects(home.featured.items);
const builtProjects = resolveProjects(home.built.items);

function Reveal({ children, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function ProjectTile({ item, href, label, className = "" }) {
  return (
    <Link
      to={href || `/projects?category=${item.project.category}&project=${item.project.id}`}
      className={`group relative block overflow-hidden bg-grey-dark ${className}`}
    >
      <img
        src={item.project.coverImage}
        alt={`${label || item.project.name}, ${item.project.location}`}
        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 text-white md:p-7">
        <p className="type-label mb-1 text-white/70">{item.project.location}</p>
        <h3 className="type-card-title">{label || item.project.name}</h3>
        <span className="type-action mt-4 inline-block text-white/80 transition-colors group-hover:text-white">
          {home.projectLinkLabel} <span aria-hidden="true">↗</span>
        </span>
      </div>
    </Link>
  );
}

function Home() {
  const [heroIndex, setHeroIndex] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => (
    typeof window !== "undefined"
      && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ));
  const [autoPlayPreference, setAutoPlayPreference] = useState("default");
  const heroProject = heroProjects[heroIndex];

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotionPreference = () => setPrefersReducedMotion(mediaQuery.matches);
    updateMotionPreference();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateMotionPreference);
      return () => mediaQuery.removeEventListener("change", updateMotionPreference);
    }
    mediaQuery.addListener(updateMotionPreference);
    return () => mediaQuery.removeListener(updateMotionPreference);
  }, []);

  const autoPlayEnabled = autoPlayPreference === "on"
    || (autoPlayPreference === "default" && !prefersReducedMotion);
  const animateHero = !prefersReducedMotion || autoPlayPreference === "on";

  useEffect(() => {
    if (!autoPlayEnabled) return undefined;

    const timer = window.setInterval(() => {
      setHeroIndex((current) => (current + 1) % heroProjects.length);
    }, 6000);

    return () => window.clearInterval(timer);
  }, [autoPlayEnabled]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.Image !== "function") return;
    const nextProject = heroProjects[(heroIndex + 1) % heroProjects.length];
    const preload = new window.Image();
    preload.decoding = "async";
    preload.src = nextProject.coverImage;
  }, [heroIndex]);

  const toggleAutoPlay = () => {
    setAutoPlayPreference(autoPlayEnabled ? "off" : "on");
  };

  return (
    <main className="flex-grow bg-white text-grey-dark">
      <section className="relative min-h-[86vh] overflow-hidden bg-grey-dark text-white">
        <AnimatePresence initial={false} mode="sync">
          <motion.img
            key={heroProject.id}
            src={heroProject.coverImage}
            alt=""
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: animateHero ? 0.9 : 0, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            decoding="async"
          />
        </AnimatePresence>
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-grey-dark/95 via-grey-dark/70 to-grey-dark/20"
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-grey-dark/60 via-transparent to-grey-dark/80"
          aria-hidden="true"
        />
        <div className="relative mx-auto flex min-h-[86vh] max-w-7xl flex-col justify-end px-6 pb-16 pt-40 md:px-10 md:pb-24">
          <div className="max-w-3xl text-white">
            <p className="type-label mb-5 text-white/85">{home.hero.eyebrow}</p>
            <h1 className="type-display">{home.hero.title}</h1>
            <p className="type-body-large mt-6 text-white/90">
              {home.hero.description}
            </p>
            <Link to={home.hero.ctaHref} className="type-action mt-8 inline-flex items-center border border-white/70 px-6 py-3 transition-colors hover:bg-white hover:text-grey-dark">
              {home.hero.ctaLabel} <span className="ml-3 text-base" aria-hidden="true">↗</span>
            </Link>
          </div>
          <div className="mt-12 flex flex-col gap-5 border-t border-white/30 pt-5 sm:mt-16 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="type-label text-white/60">{home.hero.nowViewingLabel}</p>
              <p className="type-card-title mt-2">{heroProject.name}</p>
              <p className="type-body text-white/70">{heroProject.location}</p>
            </div>
            <div className="flex w-full min-w-0 flex-col gap-4 sm:w-auto sm:flex-row sm:items-center sm:justify-end">
              <button
                type="button"
                onClick={toggleAutoPlay}
                aria-pressed={autoPlayEnabled}
                aria-label={autoPlayEnabled ? "Pause slides" : "Play slides"}
                className="type-label inline-flex min-h-9 shrink-0 items-center self-start whitespace-nowrap border border-white/50 px-3 py-2 text-white/85 transition-colors hover:border-white hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:self-auto"
              >
                {autoPlayEnabled ? home.hero.autoplayOnLabel : home.hero.autoplayOffLabel}
              </button>
              <div className="flex flex-wrap gap-1" role="group" aria-label="Featured project slides">
                {heroProjects.map((item, index) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setHeroIndex(index)}
                    aria-label={`Show ${item.name}`}
                    aria-pressed={heroIndex === index}
                    className="group flex h-8 w-8 shrink-0 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                  >
                    <span className={`block h-1 w-full transition-colors ${heroIndex === index ? "bg-white" : "bg-white/40 group-hover:bg-white/70"}`} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-6 py-20 md:px-10 lg:grid-cols-[1fr_1.15fr] lg:gap-20 lg:py-28">
        <Reveal>
          <p className="type-label text-rust">{home.about.label}</p>
          <h2 className="type-section-title mt-4 max-w-lg">{home.about.title}</h2>
        </Reveal>
        <Reveal className="max-w-2xl lg:pt-8">
          <p className="type-body-large text-grey-dark">
            {home.about.paragraphs[0]}
          </p>
          <p className="type-body mt-5 text-grey">
            {home.about.paragraphs[1]}
          </p>
          <Link to={home.about.ctaHref} className="type-action link-underline mt-7 inline-block text-rust">{home.about.ctaLabel} <span aria-hidden="true">↗</span></Link>
        </Reveal>
      </section>

      <section className="bg-grey-light/25 px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
            <div>
              <h2 className="type-section-title mt-3">{home.featured.title}</h2>
            </div>
            <Link to={home.featured.seeAllHref} className="type-action link-underline text-grey-dark">{home.featured.seeAllLabel} <span aria-hidden="true">↗</span></Link>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2">
            {featuredProjects.map((item) => <ProjectTile key={item.label} item={item} label={item.label} href={item.href} className="aspect-[4/3]" />)}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-10">
            <p className="type-label text-rust">{home.expertise.label}</p>
            <h2 className="type-section-title mt-3">{home.expertise.title}</h2>
          </Reveal>
          <div className="grid border-t border-grey-light md:grid-cols-2 lg:grid-cols-4">
            {expertise.map((item, index) => (
              <Reveal key={item.name} className="border-b border-grey-light py-7 md:border-r md:px-7 md:first:pl-0 lg:border-b-0 lg:first:pl-0 lg:last:border-r-0">
                <span className="type-label text-rust">0{index + 1}</span>
                <h3 className="type-card-title mt-10">{item.name}</h3>
                <p className="type-body mt-3 text-grey">{item.description}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-grey-dark px-6 py-20 text-white md:px-10 md:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-10">
            <h2 className="type-section-title">{home.built.title}</h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {builtProjects.map((item) => <ProjectTile key={item.label} item={item} label={item.label} href={item.href} className="aspect-[4/5]" />)}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 md:px-10 md:py-28">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-10">
            <p className="type-label text-rust">{home.team.label}</p>
            <h2 className="type-section-title mt-3">{home.team.title}</h2>
          </Reveal>
          <div className="grid gap-10 md:grid-cols-2">
            {founders.map((founder) => (
              <Reveal key={founder.name} className="grid gap-6 sm:grid-cols-[10rem_1fr] sm:items-end">
                <div className="aspect-[4/5] overflow-hidden bg-grey-light">
                  <img src={founder.image} alt={founder.name} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="pb-1">
                  <p className="type-label text-rust">{founder.role}</p>
                  <h3 className="type-card-title mt-2">{founder.name}</h3>
                  <p className="type-body mt-3 text-grey">{founder.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-rust px-6 py-16 text-center text-white md:px-10 md:py-20">
        <Reveal>
          <h2 className="type-section-title">{home.cta.title}</h2>
          <p className="type-body mx-auto mt-4 max-w-xl text-white/85">{home.cta.description}</p>
          <Link to={home.cta.href} className="type-action mt-8 inline-flex border border-white/80 px-7 py-3 transition-colors hover:bg-white hover:text-rust">{home.cta.label}</Link>
        </Reveal>
      </section>
    </main>
  );
}

export default Home;
